import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { shippingAddressesQuery, subscriptionQuery } from '../../account/queries'
import {
  actionsFor,
  knownToRecharge,
  planName,
  priceLine,
  scheduleLabel,
} from '../../account/subscription'
import { messageForBillingError } from '../../api/errors'
import type { Household } from '../../api/households'
import type { Subscription } from '../../api/subscriptions'
import type { User } from '../../api/users'
import { Button } from '../ui/Button'
import { ManageSubscription } from './ManageSubscription'
import { PaymentSetting } from './PaymentSetting'
import { ScheduleSetting } from './ScheduleSetting'
import { SettingRow, ValueSkeleton } from './Setting'
import { ShippingAddressSetting } from './ShippingAddressSetting'

interface SubscriptionSettingsProps {
  child: User
  household: Household
  /** Everyone in the household, to say whose kit ships where. */
  kids: User[]
  /** Every subscription in the household; undefined while they load. */
  subscriptions: Subscription[] | undefined
  /** The ones this child's card shows. */
  shown: Subscription[]
  error: unknown
  retry(): void
}

/**
 * The subscription rows of a child's card. The household list says which
 * subscriptions a child has and their status, and needs nothing from
 * Recharge; the rest — the next charge, the address, the card — is read live
 * for each subscription shown, and the rows fill in as it arrives.
 */
export function SubscriptionSettings({
  child,
  household,
  kids,
  subscriptions,
  shown,
  error,
  retry,
}: SubscriptionSettingsProps) {
  if (error) {
    return (
      <SettingRow
        label="Subscription"
        value="Couldn’t load"
        error={messageForBillingError(error)}
        action={
          <Button variant="secondary" onClick={retry} data-testid="subscription-retry">
            Try again
          </Button>
        }
        testId="subscription-error"
      />
    )
  }

  if (!subscriptions) {
    return (
      <SettingRow label="Subscription" value={<ValueSkeleton />} testId="subscription-loading" />
    )
  }

  if (shown.length === 0) return <NoSubscriptionRow />

  return (
    <>
      {shown.map((subscription) => (
        <SubscriptionRows
          key={subscription.id}
          subscription={subscription}
          child={child}
          household={household}
          kids={kids}
          subscriptions={subscriptions}
        />
      ))}
    </>
  )
}

/** A child with nothing to manage: no subscription, or one Recharge has yet to report. */
function NoSubscriptionRow() {
  return (
    <SettingRow
      label="Subscription"
      value="No active subscription"
      helper="Just subscribed? It can take a few minutes to show up here."
      testId="subscription-none"
    />
  )
}

interface SubscriptionRowsProps {
  subscription: Subscription
  child: User
  household: Household
  kids: User[]
  subscriptions: Subscription[]
}

/** One subscription's rows, from what Recharge says about it now. */
function SubscriptionRows({
  subscription,
  child,
  household,
  kids,
  subscriptions,
}: SubscriptionRowsProps) {
  const detail = useQuery(subscriptionQuery(subscription.id))
  // The address book waits for a subscription Recharge knows; the API has none for one it does not.
  const reported = detail.data && knownToRecharge(detail.data) ? detail.data : null
  const addresses = useQuery(shippingAddressesQuery(household, reported))
  // What the last change to the status did, in a word. A change to the status
  // swaps the schedule row for another and can take the Pause and Cancel links
  // with it, so the editor that asked for it does not survive to say "Saved"
  // or to hand focus back; these rows do, and the row that arrives says the
  // word and takes focus (see ScheduleSetting).
  const [outcome, setOutcome] = useState<string | null>(null)

  if (detail.isPending) {
    return (
      <>
        <PlanRow subscription={subscription} />
        <SettingRow
          label={scheduleLabel(subscription)}
          value={<ValueSkeleton />}
          testId="setting-schedule"
        />
      </>
    )
  }

  // One notice for whatever Recharge could not answer, in place of the rows
  // that would need it; the plan row needs nothing from it and stays.
  if (detail.isError || addresses.isError) {
    const failed = detail.isError ? detail : addresses
    return (
      <>
        <PlanRow subscription={subscription} />
        <SettingRow
          label="Billing"
          value="Unavailable right now"
          error={messageForBillingError(failed.error)}
          action={
            <Button
              variant="secondary"
              onClick={() => void failed.refetch()}
              data-testid="billing-retry"
            >
              Try again
            </Button>
          }
          testId="billing-error"
        />
      </>
    )
  }

  if (!reported) return <NoSubscriptionRow />

  const address = addresses.data?.find((candidate) =>
    candidate.subscriptionIds.includes(reported.id),
  )

  return (
    <>
      <PlanRow subscription={reported} />
      <ScheduleSetting
        subscription={reported}
        child={child}
        household={household}
        outcome={outcome}
        setOutcome={setOutcome}
      />
      {actionsFor(reported).changeAddress && (
        <ShippingAddressSetting
          subscription={reported}
          child={child}
          household={household}
          kids={kids}
          subscriptions={subscriptions}
          addresses={addresses.data}
        />
      )}
      <PaymentSetting
        subscription={reported}
        child={child}
        method={address?.paymentMethod ?? null}
        pending={addresses.isPending}
      />
      <ManageSubscription
        subscription={reported}
        child={child}
        household={household}
        setOutcome={setOutcome}
      />
    </>
  )
}

function PlanRow({ subscription }: { subscription: Subscription }) {
  const price = priceLine(subscription)
  return (
    <SettingRow
      label="Plan"
      value={planName(subscription.tier)}
      helper={price ? `${price}. To change plans, contact support.` : <ValueSkeleton />}
      testId="setting-plan"
    />
  )
}
